/**
 * cajaSessionLocal.ts — Lectura de sesión multicaja en localStorage (demo / fallback).
 *
 * Alineado con mocks `caja_sesiones_activas` y `altenot_punto_caja_id`.
 * NO importa desde mocks/ para mantener la capa features desacoplada.
 */

import {
  FALLBACK_PUNTOS_CAJA,
  LS_PUNTO_CAJA_KEY,
  type PuntoCajaListItem,
} from "../domain/puntoCaja.types";
import type { MovimientoCaja } from "../types/caja.types";
import {
  runTransaction,
  txRead,
  txReadRaw,
  txRemove,
  txWrite,
  txWriteRaw,
} from "../../../lib/persistence/transactionalStore";

const SESIONES_MAP_KEY = "caja_sesiones_activas";
/** Espejo legado de la sesión del punto seleccionado (JSON). */
const LEG_SESION_ACTIVA_KEY = "caja_sesion_activa";
/** Bandera escalar cruda "open"/"closed" leída por otros módulos con getItem directo. */
const CAJA_LAST_STATUS_KEY = "caja_last_status";
export const CAJA_PUNTOS_CATALOGO_KEY = "caja_puntos_catalogo";

export function getSelectedPuntoCajaIdFromLs(): string {
  const x = txReadRaw(LS_PUNTO_CAJA_KEY);
  if (x) return x;
  const leg = txRead<{ puntoCajaId?: string } | null>(
    LEG_SESION_ACTIVA_KEY,
    null,
  );
  if (leg?.puntoCajaId) return String(leg.puntoCajaId);
  return "pc1";
}

export function setSelectedPuntoCajaIdOnLs(id: string): void {
  txWriteRaw(LS_PUNTO_CAJA_KEY, id);
}

export function readSesionActivaForPunto(
  puntoId: string,
): Record<string, unknown> | null {
  const map = txRead<Record<string, unknown>>(SESIONES_MAP_KEY, {});
  if (map && typeof map === "object" && map[puntoId]) {
    return map[puntoId] as Record<string, unknown>;
  }
  const leg = txRead<Record<string, unknown> | null>(
    LEG_SESION_ACTIVA_KEY,
    null,
  );
  if (leg) {
    const pid = String((leg.puntoCajaId as string) || "pc1");
    if (pid === puntoId) return leg;
  }
  return null;
}

export function formatPuntoCajaDisplay(
  p?: Pick<PuntoCajaListItem, "nombre" | "alias"> | null,
): string | undefined {
  if (!p?.nombre) return undefined;
  return p.alias?.trim() ? `${p.nombre} — ${p.alias.trim()}` : p.nombre;
}

/**
 * Movimientos que pertenecen a la sesión activa, incluyendo los huérfanos
 * recuperados desde Auditoría (sin `sesionId`) del mismo punto y posteriores a
 * la apertura. Así un movimiento reconstruido sigue contando y viéndose en la
 * sesión en curso aunque haya perdido su vínculo de sesión.
 */
export function filterMovimientosSesionConRecuperados(
  movimientos: MovimientoCaja[],
  sesion: { id?: unknown; fechaApertura?: unknown; abiertaEn?: unknown } | null,
  puntoId: string,
): MovimientoCaja[] {
  if (!sesion) return [];
  const sesionId = String(sesion.id ?? "");
  const abiertaEnMs = new Date(
    String(sesion.fechaApertura ?? sesion.abiertaEn ?? ""),
  ).getTime();

  return movimientos.filter((m) => {
    if (m.sesionId === sesionId) return true;
    // Movimiento con sesión propia (otra sesión): nunca se mezcla.
    if (m.sesionId) return false;
    const puntoOk = !m.puntoCajaId || m.puntoCajaId === puntoId;
    const fechaMs = new Date(m.fecha).getTime();
    return (
      puntoOk &&
      Number.isFinite(abiertaEnMs) &&
      Number.isFinite(fechaMs) &&
      fechaMs >= abiertaEnMs
    );
  });
}

/** Catálogo multicaja persistido (compartido entre perfiles en el mismo navegador). */
export function readPuntosCatalogFromLocalStorage(): PuntoCajaListItem[] {
  const pts = txRead<PuntoCajaListItem[]>(CAJA_PUNTOS_CATALOGO_KEY, []);
  if (!Array.isArray(pts)) return [];
  return pts.filter((p) => p && p.activa !== false);
}

export function persistPuntosCatalogToLocalStorage(
  puntos: PuntoCajaListItem[],
): void {
  if (!puntos.length) return;
  txWrite(CAJA_PUNTOS_CATALOGO_KEY, puntos);
}

export function nombrePuntoFromCatalogLocal(
  puntoId: string,
): string | undefined {
  const p = readPuntosCatalogFromLocalStorage().find((x) => x.id === puntoId);
  return formatPuntoCajaDisplay(p);
}

/** Etiqueta visible del punto: lista en memoria → catálogo LS → id. */
export function resolvePuntoCajaDisplayLabel(
  puntoId: string,
  puntosEnMemoria?: PuntoCajaListItem[],
): string {
  const fromMem = formatPuntoCajaDisplay(
    puntosEnMemoria?.find((p) => p.id === puntoId) ?? null,
  );
  if (fromMem) return fromMem;
  const fromLs = nombrePuntoFromCatalogLocal(puntoId);
  if (fromLs) return fromLs;
  const fb = FALLBACK_PUNTOS_CAJA.find((p) => p.id === puntoId);
  return formatPuntoCajaDisplay(fb) || puntoId;
}

/** Actualiza el nombre visible en sesiones abiertas tras cambiar alias en el catálogo. */
export function syncPuntoNombreEnSesionesActivas(puntoId: string): void {
  const label = nombrePuntoFromCatalogLocal(puntoId);
  if (!label) return;
  runTransaction((): void => {
    const map = txRead<Record<string, Record<string, unknown>>>(
      SESIONES_MAP_KEY,
      {},
    );
    if (map?.[puntoId]) {
      map[puntoId] = { ...map[puntoId], puntoCajaNombre: label };
      txWrite(SESIONES_MAP_KEY, map);
    }
    const leg = txRead<Record<string, unknown> | null>(
      LEG_SESION_ACTIVA_KEY,
      null,
    );
    if (leg && String((leg.puntoCajaId as string) || "pc1") === puntoId) {
      txWrite(LEG_SESION_ACTIVA_KEY, { ...leg, puntoCajaNombre: label });
    }
  });
}

/** Puntos que tienen sesión abierta en el mapa local (demo / fallback). */
export function listOpenSessionPuntosFromLs(
  catalog: { id: string; nombre: string }[],
): { id: string; nombre: string }[] {
  const map = txRead<Record<string, { puntoCajaNombre?: string }>>(
    SESIONES_MAP_KEY,
    {},
  );
  if (!map || typeof map !== "object") return [];
  return Object.keys(map).map((id) => ({
    id,
    nombre:
      nombrePuntoFromCatalogLocal(id) ||
      map[id]?.puntoCajaNombre ||
      formatPuntoCajaDisplay(catalog.find((p) => p.id === id)) ||
      id,
  }));
}

/**
 * Tras cerrar caja: quita la sesión del mapa y re-sincroniza el espejo legado.
 * Evita que un reload muestre la caja abierta si el SW no persistió a tiempo.
 */
/**
 * Tras POST /caja/abrir: si la sesión aún no está en LS (p. ej. red parcial),
 * escribe el mismo shape que los mocks para que `readSesionActivaForPunto` y la UI coincidan.
 */
export function persistOpenSesionIfMissing(
  puntoId: string,
  payload: {
    montoInicial: number;
    usuarioId?: string;
    userName?: string;
    userEmail?: string;
    baseApertura?: import("../types/caja.types").BaseAperturaTurno;
  },
): void {
  runTransaction((): void => {
    if (readSesionActivaForPunto(puntoId)) return;
    const puntoCajaNombre = nombrePuntoFromCatalogLocal(puntoId);
    const nuevaSesion = {
      id: `sesion_${Date.now()}`,
      fechaApertura: new Date().toISOString(),
      montoInicial: payload.montoInicial,
      estado: "abierta",
      openedBy: payload.usuarioId ?? "unknown",
      openedByName: payload.userName ?? "Desconocido",
      openedByEmail: payload.userEmail,
      puntoCajaId: puntoId,
      ...(payload.baseApertura ? { baseApertura: payload.baseApertura } : {}),
      ...(puntoCajaNombre ? { puntoCajaNombre } : {}),
    };
    const map = txRead<Record<string, unknown>>(SESIONES_MAP_KEY, {});
    map[puntoId] = nuevaSesion;
    txWrite(SESIONES_MAP_KEY, map);
    txWrite(LEG_SESION_ACTIVA_KEY, { ...nuevaSesion, puntoCajaId: puntoId });
  });
}

/**
 * Si no hay sesión persistida para el punto pero quedó `caja_last_status=open`,
 * el flag quedó huérfano (p. ej. borrado parcial de datos del sitio) → lo cerramos
 * para que el bootstrap no asuma caja abierta sin respaldo en LS.
 */
export function reconcileCajaLastStatusOpenFlag(puntoCajaId: string): void {
  runTransaction((): void => {
    if (readSesionActivaForPunto(puntoCajaId)) return;
    if (txReadRaw(CAJA_LAST_STATUS_KEY) === "open") {
      txWriteRaw(CAJA_LAST_STATUS_KEY, "closed");
    }
  });
}

// ─── Modo local-first (demo / sin backend remoto) ───────────────────────────

export function isCajaRemoteApiEnabled(): boolean {
  return import.meta.env.VITE_DEV_USE_REMOTE_API === "true";
}

export function shouldPreserveLocalSessionOverRemoteClosed(
  puntoId: string,
  apiSaysClosed: boolean,
): boolean {
  return (
    apiSaysClosed &&
    !isCajaRemoteApiEnabled() &&
    readSesionActivaForPunto(puntoId) !== null
  );
}

function isDemoAuthToken(token?: string | null): boolean {
  return !!token?.includes("mock-token");
}

/** Fallback de POST /caja/abrir ante 404 o red cuando no hay API remota activa. */
export function shouldFallbackCajaOpenBoxToLocal(error: unknown): boolean {
  if (isCajaRemoteApiEnabled()) return false;

  const axiosErr = error as {
    response?: { status?: number };
    config?: { headers?: Record<string, string> };
  };

  const authHeader = axiosErr.config?.headers?.["Authorization"] ?? "";
  if (isDemoAuthToken(authHeader)) return true;
  if (!axiosErr.response) return true;

  const status = axiosErr.response.status;
  return (
    status === 404 ||
    status === 401 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
}

export function clearLocalSesionForPunto(puntoId: string): void {
  runTransaction((): void => {
    const map = txRead<Record<string, Record<string, unknown>>>(
      SESIONES_MAP_KEY,
      {},
    );
    if (map && typeof map === "object" && !Array.isArray(map)) {
      delete map[puntoId];
    }
    const keys = Object.keys(map);
    if (keys.length === 0) {
      txRemove(SESIONES_MAP_KEY);
      txRemove(LEG_SESION_ACTIVA_KEY);
      txWriteRaw(CAJA_LAST_STATUS_KEY, "closed");
      return;
    }
    txWrite(SESIONES_MAP_KEY, map);
    const cur = txReadRaw(LS_PUNTO_CAJA_KEY);
    const pid = cur || keys[0];
    const sesion = map[pid];
    if (sesion) {
      txWrite(LEG_SESION_ACTIVA_KEY, { ...sesion, puntoCajaId: pid });
    } else {
      txRemove(LEG_SESION_ACTIVA_KEY);
    }
  });
}
